export interface PaginationProbe {
  //true when the content of the page number `pageIndex`, showing this text (and the statement when asked), fits in the page
  fits: (pageIndex: number, text: string, withStatement: boolean) => boolean;
}

export interface PaginationResult {
  //the comments text of each page, in order. A single empty string when there are no comments
  chunks: string[];
  //true when the statement of limiting conditions does not fit after the last chunk and goes on a page of its own
  statementOnItsOwnPage: boolean;
}

const MAX_PAGES = 60;

//Splits the value reconciliation comments into the text of each page. Every page gets as many words as fit,
//the statement is always the last thing: after the last chunk when it fits there, otherwise on its own page.
export function paginateComments(text: string, probe: PaginationProbe): PaginationResult {
  const words = (text || "").split(/\s+/).filter(Boolean);

  if (words.length === 0) {
    return { chunks: [""], statementOnItsOwnPage: !probe.fits(0, "", true) };
  }

  const chunks: string[] = [];
  let start = 0;

  for (let page = 0; page < MAX_PAGES; page++) {
    const remaining = words.slice(start);
    const remainingText = remaining.join(" ");

    if (probe.fits(page, remainingText, true)) {
      chunks.push(remainingText);
      return { chunks, statementOnItsOwnPage: false };
    }
    if (probe.fits(page, remainingText, false)) {
      chunks.push(remainingText);
      return { chunks, statementOnItsOwnPage: true };
    }

    //most words that fit in this page (without the statement), always at least one so it keeps moving forward
    let low = 1;
    let high = remaining.length - 1;
    let best = 1;
    while (low <= high) {
      const middle = Math.floor((low + high) / 2);
      if (probe.fits(page, remaining.slice(0, middle).join(" "), false)) {
        best = middle;
        low = middle + 1;
      } else {
        high = middle - 1;
      }
    }

    chunks.push(remaining.slice(0, best).join(" "));
    start += best;
    if (start >= words.length) {
      return { chunks, statementOnItsOwnPage: true };
    }
  }

  //safety net: never loses text, the rest goes in the last page
  chunks.push(words.slice(start).join(" "));
  return { chunks, statementOnItsOwnPage: true };
}
